import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAddonBalances, usePurchaseAddon } from '@/hooks/addons';
import { LoadingState } from '@/components/LoadingState';
import { Coins, HardDrive, ShoppingCart, RefreshCw } from 'lucide-react';
import type { UserAddonBalances } from '@shared/schema';

interface AddonBalanceDisplayProps {
  className?: string;
  showQuickTopUp?: boolean;
  'data-testid'?: string;
}

export function AddonBalanceDisplay({ 
  className = '', 
  showQuickTopUp = true,
  'data-testid': testId 
}: AddonBalanceDisplayProps) {
  const { data: balancesResponse, isLoading, refetch } = useAddonBalances();
  const purchaseAddon = usePurchaseAddon();

  if (isLoading) {
    return (
      <Card className={className} data-testid={testId}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Add-on Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingState message="Loading balances..." />
        </CardContent>
      </Card>
    );
  }

  const balances = balancesResponse?.balances;

  if (!balances) {
    return (
      <Card className={className} data-testid={testId}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Add-on Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unable to load balances</p>
        </CardContent>
      </Card>
    );
  }

  const handleQuickPurchase = (sku: string, quantity: number = 1) => {
    purchaseAddon.mutate({
      items: [{ sku, quantity }]
    });
  };

  const quickTopUpOptions = [
    { sku: 'illustrations_25', label: '25 Illustrations', icon: Coins },
    { sku: 'illustrations_50', label: '50 Illustrations', icon: Coins },
    { sku: 'storage_5gb', label: '5GB Storage', icon: HardDrive },
    { sku: 'storage_unlimited', label: 'Unlimited Storage', icon: HardDrive },
  ];

  return (
    <Card className={`transition-all duration-200 hover:shadow-md ${className}`} data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Add-on Balances
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            data-testid={`${testId}-refresh-button`}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Balances */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Illustration Credits */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium">Illustration Credits</span>
            </div>
            <div className="flex items-center justify-between">
              <span 
                className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent"
                data-testid={`${testId}-illustration-credits`}
              >
                {balances.illustrationCredits === 'unlimited' ? 
                  'Unlimited' : 
                  balances.illustrationCredits.toLocaleString()
                }
              </span>
              <Badge variant={balances.illustrationCredits === 'unlimited' || balances.illustrationCredits > 10 ? "default" : "destructive"}>
                {balances.illustrationCredits === 'unlimited' ? 
                  'Unlimited' : 
                  balances.illustrationCredits > 10 ? "Good" : "Low"
                }
              </Badge>
            </div>
          </div>

          {/* Storage */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-teal-600" />
              <span className="text-sm font-medium">
                {balances.subscriptionStatus === 'lifetime' ? 'Storage Access' : 'Extra Storage'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              {balances.hasUnlimitedStorage || balances.extraStorageGb === 'unlimited' ? (
                <span 
                  className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent"
                  data-testid={`${testId}-storage-unlimited`}
                >
                  Unlimited
                </span>
              ) : (
                <span 
                  className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent"
                  data-testid={`${testId}-storage-gb`}
                >
                  {balances.extraStorageGb}GB
                </span>
              )}
              <Badge variant={balances.hasUnlimitedStorage || balances.extraStorageGb === 'unlimited' || balances.extraStorageGb > 5 ? "default" : "secondary"}>
                {balances.hasUnlimitedStorage || balances.extraStorageGb === 'unlimited' ? 
                  "Unlimited" : 
                  balances.extraStorageGb > 5 ? "Good" : "Basic"
                }
              </Badge>
            </div>
          </div>
        </div>

        {/* Quick Top-up Options - Hidden for lifetime users */}
        {showQuickTopUp && balances.subscriptionStatus !== 'lifetime' && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Quick Top-up
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quickTopUpOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <Button
                      key={option.sku}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickPurchase(option.sku)}
                      disabled={purchaseAddon.isPending}
                      className="justify-start gap-2 h-auto py-2"
                      data-testid={`${testId}-quick-topup-${option.sku}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-xs">{option.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Lifetime Benefits Message */}
        {balances.subscriptionStatus === 'lifetime' && (
          <>
            <Separator />
            <div className="text-center py-4 space-y-2">
              <div className="text-lg font-semibold text-gradient bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                🎉 Lifetime Access Benefits
              </div>
              <p className="text-sm text-muted-foreground">
                You have unlimited illustrations and storage forever! No need for additional purchases.
              </p>
            </div>
          </>
        )}

        {/* Last Updated */}
        <div className="text-xs text-muted-foreground text-center">
          Last updated: {new Date(balances.updatedAt).toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}